import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../App';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, Wrench, RotateCcw, Settings, FileText, HardHat, Users,
  Search, Award, Cog, BarChart3, Shield, Check, Building, Send,
  Calendar, DollarSign, Package, Briefcase, Star, ChevronLeft, ChevronRight, MapPin
} from 'lucide-react';
import Footer from '../components/Footer';
import { formatPrice, getCurrencyByLocation } from '../utils/currency';
import { getFallbackImage } from '../utils/fallbackImages';

export default function HomeV2() {
  const { i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const isFr = i18n.language === 'fr';
  
  // Featured products state
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef(null);
  
  // Fetch featured products
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?featured=true`);
        const featured = response.data.filter(p => p.featured === true);
        setFeaturedProducts(featured);
      } catch (error) {
        console.error('Error fetching featured products:', error);
      }
    };
    fetchFeaturedProducts();
  }, [API]);
  
  // Auto-scroll carousel
  useEffect(() => {
    if (featuredProducts.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % featuredProducts.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [featuredProducts.length]);
  
  // Carousel navigation
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % featuredProducts.length);
  };
  
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + featuredProducts.length) % featuredProducts.length);
  };
  
  // Get listing badge for product
  const getListingBadge = (type) => {
    const badges = {
      neuf: { label: 'NEUF', color: 'bg-blue-500', icon: '✨' },
      occasion: { label: 'OCCASION', color: 'bg-amber-500', icon: '🔄' },
      reconditionne: { label: 'RECONDITIONNÉ', color: 'bg-green-500', icon: '♻️' },
      location: { label: 'LOCATION', color: 'bg-purple-500', icon: '📅' },
      service: { label: 'SERVICE', color: 'bg-emerald-500', icon: '🔧' },
      travaux: { label: 'TRAVAUX', color: 'bg-orange-500', icon: '🏗️' },
      etude: { label: 'ÉTUDE', color: 'bg-cyan-500', icon: '📊' },
      talent: { label: 'TALENT', color: 'bg-pink-500', icon: '👤' },
      presentation: { label: 'PRÉSENTATION', color: 'bg-indigo-500', icon: '🎯' },
    };
    return badges[type] || null;
  };

  // 6 domaines d'expertise avec 3 sous-cards chacun
  const services = [
    {
      id: 'vente',
      title: isFr ? 'Achat & Vente de matériel' : 'Equipment Buy & Sell',
      description: isFr 
        ? "Foreuses, pompes, accessoires d'occasion ou neufs. On gère votre vente de A à Z." 
        : "Drilling rigs, pumps, new or used accessories. We manage your sale from A to Z.",
      icon: Wrench,
      iconBg: 'bg-[#635BFF]/20',
      iconColor: 'text-[#635BFF]',
      borderColor: 'border-l-[#635BFF]',
      subCards: [
        { icon: Users, label: isFr ? 'Vendeurs' : 'Sellers', link: '/annuaire/materiel-equipements-forage' },
        { icon: Package, label: isFr ? 'Catalogue' : 'Catalog', link: '/shop?listing_type=neuf,occasion' },
        { icon: DollarSign, label: isFr ? 'Proposer' : 'Offer', link: '/vendre' },
      ]
    },
    {
      id: 'location',
      title: isFr ? 'Location courte & longue durée' : 'Short & Long Term Rental',
      description: isFr 
        ? "Foreuses, équipements et matériel d'occasion. Solutions adaptées à vos chantiers." 
        : "Drilling rigs, equipment and used materials. Solutions adapted to your projects.",
      icon: RotateCcw,
      iconBg: 'bg-[#635BFF]/20',
      iconColor: 'text-[#635BFF]',
      borderColor: 'border-l-[#635BFF]',
      subCards: [
        { icon: Users, label: isFr ? 'Loueurs' : 'Renters', link: '/annuaire/materiel-equipements-forage' },
        { icon: Package, label: isFr ? 'Disponible' : 'Available', link: '/shop?listing_type=location' },
        { icon: Calendar, label: isFr ? 'Proposer' : 'Offer', link: '/louer-mon-materiel' },
      ]
    },
    {
      id: 'maintenance',
      title: isFr ? 'Maintenance & Étalonnage' : 'Maintenance & Calibration',
      description: isFr 
        ? "Étalonnage instrumentation, maintenance foreuses, formation opérateurs. Experts certifiés." 
        : "Instrumentation calibration, drilling rig maintenance, operator training. Certified experts.",
      icon: Settings,
      iconBg: 'bg-emerald-500/20',
      iconColor: 'text-emerald-500',
      borderColor: 'border-l-emerald-500',
      subCards: [
        { icon: Users, label: isFr ? 'Prestataires' : 'Providers', link: '/annuaire/conseil-recrutement-freelance' },
        { icon: Wrench, label: isFr ? 'Services' : 'Services', link: '/shop?listing_type=service' },
        { icon: Send, label: isFr ? 'Demander' : 'Request', link: '/service-technique' },
      ]
    },
    {
      id: 'etudes',
      title: isFr ? 'Études géotechniques' : 'Geotechnical Studies',
      description: isFr 
        ? "Missions G1 à G5 par BET certifiés. Sondages, essais in-situ, expertises spécialisées." 
        : "G1 to G5 missions by certified engineering firms. Drilling, in-situ tests, specialized expertise.",
      icon: FileText,
      iconBg: 'bg-cyan-500/20',
      iconColor: 'text-cyan-500',
      borderColor: 'border-l-cyan-500',
      subCards: [
        { icon: Building, label: 'BET', link: '/annuaire/ingenierie-etudes-geotechniques' },
        { icon: FileText, label: 'G1-G5', link: '/shop?listing_type=etude' },
        { icon: Send, label: isFr ? 'Demander' : 'Request', link: '/etude-geotechnique' },
      ]
    },
    {
      id: 'travaux',
      title: isFr ? 'Fondations spéciales' : 'Special Foundations',
      description: isFr 
        ? "Micropieux, jet grouting, parois moulées. Entreprises spécialisées pour vos projets." 
        : "Micropiles, jet grouting, diaphragm walls. Specialized companies for your projects.",
      icon: HardHat,
      iconBg: 'bg-orange-500/20',
      iconColor: 'text-orange-500',
      borderColor: 'border-l-orange-500',
      subCards: [
        { icon: Building, label: isFr ? 'Entreprises' : 'Companies', link: '/annuaire/travaux-fondations-speciales' },
        { icon: HardHat, label: isFr ? 'Travaux' : 'Works', link: '/shop?listing_type=travaux' },
        { icon: FileText, label: isFr ? 'Devis' : 'Quote', link: '/travaux-fondations' },
      ]
    },
    {
      id: 'personnel',
      title: isFr ? 'Personnel qualifié géotech' : 'Qualified Geotech Staff',
      description: isFr 
        ? "Opérateurs foreuses, mécaniciens, techniciens, ingénieurs. Profils vérifiés." 
        : "Drilling operators, mechanics, technicians, engineers. Verified profiles.",
      icon: Users,
      iconBg: 'bg-rose-500/20',
      iconColor: 'text-rose-500',
      borderColor: 'border-l-rose-500',
      subCards: [
        { icon: Briefcase, label: isFr ? 'Offres' : 'Jobs', link: '/shop?listing_type=talent' },
        { icon: Users, label: isFr ? 'Talents' : 'Talents', link: '/talents' },
        { icon: Send, label: isFr ? 'Publier' : 'Post', link: '/recrutement' },
      ]
    },
  ];

  // Points de confiance
  const trustPoints = [
    {
      icon: Award,
      iconBg: 'bg-[#635BFF]/20',
      iconColor: 'text-[#635BFF]',
      title: isFr ? "15 ans d'expertise terrain" : "15 years field expertise",
      subtitle: isFr ? "Connaissance approfondie du métier" : "Deep industry knowledge"
    },
    {
      icon: Cog,
      iconBg: 'bg-[#635BFF]/20',
      iconColor: 'text-[#635BFF]',
      title: isFr ? "Service complet de A à Z" : "Complete A to Z service",
      subtitle: isFr ? "Du sourcing à la livraison" : "From sourcing to delivery"
    },
    {
      icon: BarChart3,
      iconBg: 'bg-[#635BFF]/20',
      iconColor: 'text-[#635BFF]',
      title: isFr ? "Tarifs transparents" : "Transparent pricing",
      subtitle: isFr ? "Pas de frais cachés" : "No hidden fees"
    },
    {
      icon: Shield,
      iconBg: 'bg-[#635BFF]/20',
      iconColor: 'text-[#635BFF]',
      title: isFr ? "Pas d'inscription obligatoire" : "No registration required",
      subtitle: isFr ? "Demande simple et directe" : "Simple and direct request"
    },
    {
      icon: Check,
      iconBg: 'bg-emerald-500/20',
      iconColor: 'text-emerald-500',
      title: isFr ? "Vous ne payez QUE si on délivre" : "You pay ONLY if we deliver",
      subtitle: isFr ? "Engagement résultat" : "Results commitment"
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A2540]">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 px-4 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-10 left-10 w-96 h-96 bg-[#635BFF]/10 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-[80px]"></div>
        </div>

        <div className="container mx-auto max-w-4xl text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2.5 mb-8 border border-white/10">
            <span className="text-yellow-400 text-lg">✨</span>
            <span className="text-sm md:text-base text-gray-300">
              {isFr ? 'Spécialiste géotechnique & fondations spéciales' : 'Geotechnical & special foundations specialist'}
            </span>
          </div>

          {/* Main Title */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {isFr ? 'La plateforme B2B 100% spécialisée' : 'The 100% specialized B2B platform'}<br />
            <span className="bg-gradient-to-r from-[#635BFF] via-[#8B85FF] to-[#00D4FF] bg-clip-text text-transparent">
              {isFr ? 'géotechnique & fondations spéciales' : 'geotechnical & special foundations'}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-gray-300 mb-3">
            {isFr 
              ? 'Matériel. Accessoires. Études de sol. Travaux. Personnel qualifié.' 
              : 'Equipment. Accessories. Soil studies. Works. Qualified staff.'}
          </p>
          <p className="text-base md:text-lg text-gray-400 mb-10">
            {isFr ? 'Votre partenaire de A à Z.' : 'Your A to Z partner.'}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button
              onClick={() => navigate('/shop')}
              size="lg"
              className="bg-[#635BFF] hover:bg-[#7C75FF] text-white px-8 py-6 text-lg font-semibold rounded-xl w-full sm:w-auto transition-all duration-300"
            >
              {isFr ? 'Explorer le catalogue' : 'Browse catalog'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/inscription')}
              size="lg"
              variant="outline"
              className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl w-full sm:w-auto transition-all duration-300"
            >
              {isFr ? 'Devenir partenaire' : 'Become a partner'}
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Products Carousel */}
      {featuredProducts.length > 0 && (
        <section className="py-12 px-4 bg-gradient-to-b from-[#0A2540] to-[#0f3460]">
          <div className="container mx-auto max-w-6xl">
            {/* Section Title */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-white">
                    {isFr ? 'Produits en vedette' : 'Featured Products'}
                  </h2>
                  <p className="text-gray-400 text-sm">
                    {isFr ? 'Sélection de nos meilleures offres' : 'Selection of our best offers'}
                  </p>
                </div>
              </div>
              
              {/* Navigation Arrows */}
              {featuredProducts.length > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={prevSlide}
                    className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                </div>
              )}
            </div>

            {/* Carousel */}
            <div className="relative overflow-hidden rounded-2xl" ref={carouselRef}>
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {featuredProducts.map((product) => {
                  const productName = isFr ? product.name_fr : product.name_en || product.name;
                  const fallbackImg = getFallbackImage(productName);
                  const validImages = (product.images || []).filter(img => img && img.trim() !== '');
                  const imgSrc = validImages[0] || fallbackImg;
                  const currency = getCurrencyByLocation(product.location);
                  const badge = getListingBadge(product.listing_type);
                  
                  const getMainPrice = () => {
                    if (product.show_price === false) return { type: 'contact', label: isFr ? 'Prix sur demande' : 'Price on request' };
                    if (product.price_used) return { type: 'used', value: product.price_used };
                    if (product.price_new) return { type: 'new', value: product.price_new };
                    if (product.price_rental) return { type: 'rental', value: product.price_rental };
                    return { type: 'quote', label: isFr ? 'Sur devis' : 'Quote' };
                  };
                  const mainPrice = getMainPrice();
                  
                  return (
                    <div key={product.id} className="w-full flex-shrink-0 px-1">
                      <Link to={`/product/${product.id}`}>
                        <div className="bg-[#1a3a5c]/70 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-[#635BFF]/50 transition-all duration-300 group">
                          <div className="flex flex-col md:flex-row">
                            {/* Image */}
                            <div className="md:w-1/2 relative">
                              <img 
                                src={imgSrc}
                                alt={productName}
                                className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = fallbackImg;
                                }}
                              />
                              {/* Badges */}
                              <div className="absolute top-4 left-4 flex flex-col gap-2">
                                <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg flex items-center gap-1">
                                  <Star className="w-3 h-3" /> {isFr ? 'VEDETTE' : 'FEATURED'}
                                </span>
                                {badge && (
                                  <span className={`${badge.color} text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg`}>
                                    {badge.icon} {badge.label}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Content */}
                            <div className="md:w-1/2 p-6 flex flex-col justify-between">
                              <div>
                                {product.brand && (
                                  <span className="text-[#635BFF] text-sm font-semibold uppercase tracking-wide">
                                    {product.brand}
                                  </span>
                                )}
                                <h3 className="text-2xl font-bold text-white mt-1 mb-3 group-hover:text-[#635BFF] transition-colors">
                                  {productName}
                                </h3>
                                
                                {product.location && (
                                  <div className="flex items-center gap-2 text-gray-400 mb-3">
                                    <MapPin className="w-4 h-4" />
                                    <span className="text-sm">{product.location}</span>
                                  </div>
                                )}
                                
                                <p className="text-gray-300 text-sm line-clamp-3 mb-4">
                                  {isFr ? product.description_fr : product.description_en}
                                </p>
                                
                                {/* Specs */}
                                <div className="flex flex-wrap gap-2 mb-4">
                                  {product.brand && (
                                    <span className="bg-white/10 text-gray-300 px-3 py-1 rounded-full text-xs">
                                      {product.brand}
                                    </span>
                                  )}
                                  {product.model && (
                                    <span className="bg-white/10 text-gray-300 px-3 py-1 rounded-full text-xs">
                                      {product.model}
                                    </span>
                                  )}
                                  {product.year && (
                                    <span className="bg-white/10 text-gray-300 px-3 py-1 rounded-full text-xs">
                                      {product.year}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Price & CTA */}
                              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                                <div>
                                  {mainPrice.value ? (
                                    <>
                                      <span className="text-3xl font-bold text-white">
                                        {formatPrice(mainPrice.value, currency)}
                                      </span>
                                      <span className="text-gray-400 text-sm ml-1">
                                        {mainPrice.type === 'rental' ? '/jour HT' : 'HT'}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-xl font-semibold text-gray-300">{mainPrice.label}</span>
                                  )}
                                </div>
                                <div className="bg-[#635BFF] hover:bg-[#7C75FF] text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all">
                                  {isFr ? 'Voir détails' : 'View details'}
                                  <ArrowRight className="w-4 h-4" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
              
              {/* Dots indicator */}
              {featuredProducts.length > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {featuredProducts.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide 
                          ? 'bg-[#635BFF] w-6' 
                          : 'bg-white/30 hover:bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* View all button */}
            <div className="text-center mt-8">
              <Button
                onClick={() => navigate('/shop')}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-6 py-3"
              >
                {isFr ? 'Voir tout le catalogue' : 'View full catalog'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* 6 Domaines d'expertise */}
      <section className="py-16 px-4 bg-[#0A2540]">
        <div className="container mx-auto max-w-5xl">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              {isFr ? "Nos 6 domaines d'expertise" : "Our 6 areas of expertise"}
            </h2>
            <p className="text-gray-400">
              {isFr 
                ? "Choisissez le service adapté à votre besoin. On gère tout de A à Z." 
                : "Choose the service that fits your needs. We handle everything A to Z."}
            </p>
          </div>

          {/* Service Cards */}
          <div className="space-y-4">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <div 
                  key={service.id}
                  className={`bg-[#1a3a5c]/50 backdrop-blur-sm rounded-2xl p-6 border-l-4 ${service.borderColor} border border-white/5`}
                >
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-12 h-12 ${service.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 ${service.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{service.title}</h3>
                      <p className="text-gray-400 text-sm">{service.description}</p>
                    </div>
                  </div>

                  {/* Sub Cards */}
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {service.subCards.map((subCard, idx) => {
                      const SubIcon = subCard.icon;
                      return (
                        <Link
                          key={idx}
                          to={subCard.link}
                          className="bg-[#0A2540]/50 hover:bg-[#0A2540]/80 rounded-xl p-4 text-center transition-all duration-300 border border-white/5 hover:border-white/10"
                        >
                          <SubIcon className={`w-5 h-5 mx-auto mb-2 ${service.iconColor}`} />
                          <span className="text-gray-300 text-sm">{subCard.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section Confiance */}
      <section className="py-16 px-4 bg-gradient-to-b from-[#0A2540] to-[#0f3460]">
        <div className="container mx-auto max-w-4xl">
          {/* Section Title */}
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-10">
            {isFr ? 'Pourquoi nous faire confiance ?' : 'Why trust us?'}
          </h2>

          {/* Trust Points */}
          <div className="space-y-4">
            {trustPoints.map((point, idx) => {
              const Icon = point.icon;
              return (
                <div 
                  key={idx}
                  className="bg-[#1a3a5c]/50 backdrop-blur-sm rounded-xl p-5 flex items-center gap-4 border border-white/5"
                >
                  <div className={`w-12 h-12 ${point.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-6 h-6 ${point.iconColor}`} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{point.title}</h3>
                    <p className="text-gray-400 text-sm">{point.subtitle}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4 bg-[#0f3460]">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {isFr ? 'Prêt à démarrer ?' : 'Ready to start?'}
          </h2>
          <p className="text-gray-400 mb-8">
            {isFr 
              ? "Contactez-nous ou explorez notre catalogue dès maintenant." 
              : "Contact us or explore our catalog now."}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/shop')}
              size="lg"
              className="bg-[#635BFF] hover:bg-[#7C75FF] text-white px-8 py-5"
            >
              <Search className="w-5 h-5 mr-2" />
              {isFr ? 'Catalogue' : 'Catalog'}
            </Button>
            <Button
              onClick={() => navigate('/commissions')}
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-5"
            >
              {isFr ? 'Voir nos tarifs' : 'See our rates'}
            </Button>
            <Button
              onClick={() => navigate('/comparateur')}
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-5"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              {isFr ? 'Comparateur' : 'Comparator'}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
